import {
  faMagnifyingGlass,
  faTriangleExclamation
} from "@fortawesome/pro-regular-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type PropsWithChildren } from "react";

import { cn } from "~/lib/utils";

/**
 * Centered column for the discovery page's error / empty states. Compose an
 * icon, a `<DiscoveryState.Title>`, and optional action(s) as children.
 */
const DiscoveryStateRoot = ({ children }: PropsWithChildren) => (
  <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
    {children}
  </div>
);

const DiscoveryStateTitle = ({ children }: PropsWithChildren) => (
  <p className="text-2xl font-bold text-interface-neutral-6">{children}</p>
);

export const DiscoveryState = Object.assign(DiscoveryStateRoot, {
  Title: DiscoveryStateTitle
});

const StateIconCircle = ({
  className,
  children
}: PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      "relative flex size-24 items-center justify-center rounded-full",
      className
    )}
  >
    {children}
  </div>
);

/** Light-pink circle + red warning triangle — a data endpoint failed to load. */
export const DiscoveryErrorIcon = () => (
  <StateIconCircle className="bg-interface-negative-1">
    <FontAwesomeIcon
      icon={faTriangleExclamation}
      className="text-4xl text-interface-negative-2"
    />
  </StateIconCircle>
);

/** Gray circle + magnifying glass with a red circle-× badge — nothing matched. */
export const DiscoveryEmptyIcon = () => (
  <StateIconCircle className="bg-interface-neutral-2">
    <FontAwesomeIcon
      icon={faMagnifyingGlass}
      className="text-4xl text-interface-neutral-4"
    />
    <span className="absolute -bottom-1 -right-1 flex size-7 items-center justify-center rounded-full bg-interface-neutral-0 text-interface-negative-2 shadow-sm">
      <FontAwesomeIcon icon={faXmark} className="text-sm" />
    </span>
  </StateIconCircle>
);
